import React, { useEffect, useState } from 'react';
import { differenceInMinutes } from 'date-fns';
import moment from 'moment-timezone';
import {
    AutoComplete,
    DatePicker,
    FieldGroup,
    Grid,
    Modal,
    PillButton,
    Row,
    Select,
    Stack,
    Text,
    TimePicker,
} from 'library/ui';
import { Add } from '@mui/icons-material';
import { client, consoleLogInDev } from 'shared';
import { buildTimeInZone } from 'shared/shiftTime';
import { IBusinessLocation, PagingResult } from 'models';
import {
    getPastWorkers,
    PastWorkerDTO,
} from 'parent-portal/forms/components/pages/ProviderPreferencesPage/preferredWorkersApi';

function timeStringToMoment(value: string, current?: moment.Moment | null) {
    const [hour, minute] = value.split(':').map(Number);
    return (current ?? moment()).clone().set({ hour, minute, second: 0, millisecond: 0 });
}

export function AddRetroHoursModal({
    onComplete,
    businessLocations,
}: {
    onComplete: () => void;
    businessLocations?: PagingResult<IBusinessLocation>;
}) {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [selectedLocation, setSelectedLocation] = useState<IBusinessLocation>();
    const [date, setDate] = useState<moment.Moment | null>(moment());
    const [startTime, setStartTime] = useState<moment.Moment | null>(moment());
    const [endTime, setEndTime] = useState<moment.Moment | null>(moment());
    const [breakStart, setBreakStart] = useState<moment.Moment | null>();
    const [breakEnd, setBreakEnd] = useState<moment.Moment | null>();
    const [pastWorkers, setPastWorkers] = useState<PastWorkerDTO[]>([]);
    const [selectedWorker, setSelectedWorker] = useState<PastWorkerDTO | null>(null);

    useEffect(() => {
        if (!isOpen || !selectedLocation) {
            return;
        }

        getPastWorkers(undefined, selectedLocation ? [selectedLocation.address.id] : undefined, true, true)
            .then(setPastWorkers)
            .catch(consoleLogInDev);
    }, [isOpen, selectedLocation]);

    function onAdd() {
        const tz = selectedLocation?.timezone;
        if (!date || !startTime || !tz) return;

        const dateStr = date.format('YYYY-MM-DD');
        const start_time = buildTimeInZone(startTime.format('HH:mm'), dateStr, tz);
        const end_time = endTime ? buildTimeInZone(endTime.format('HH:mm'), dateStr, tz) : undefined;
        const break_start = breakStart ? buildTimeInZone(breakStart.format('HH:mm'), dateStr, tz) : undefined;
        const break_end = breakEnd ? buildTimeInZone(breakEnd.format('HH:mm'), dateStr, tz) : undefined;

        setErrorMessage('');
        setSubmitting(true);

        client('api/hour-report/retro-add-hours/', {
            body: {
                start_time,
                end_time,
                break_start,
                break_end,
                business_location_id: selectedLocation?.id,
                provider_id: selectedWorker?.babysitter_id,
            },
        })
            .then(() => {
                onClose();
                onComplete();
            })
            .catch((error) =>
                setErrorMessage(error.message || 'Sorry, something went wrong. Please try again or contact support.'),
            )
            .finally(() => setSubmitting(false));
    }

    function onClose() {
        setIsOpen(false);
        setSelectedWorker(null);
        setSelectedLocation(undefined);
        setDate(moment());
        setStartTime(moment());
        setEndTime(moment());
        setBreakStart(undefined);
        setBreakEnd(undefined);
    }

    function areTimeValid() {
        if (endTime && startTime && endTime < startTime) {
            return false;
        }

        if (breakStart && breakEnd && breakEnd < breakStart) {
            return false;
        }

        if (
            (startTime && breakStart && breakStart < startTime) ||
            (startTime && breakEnd && breakEnd < startTime) ||
            (endTime && breakStart && breakStart > endTime) ||
            (endTime && breakEnd && breakEnd > endTime)
        ) {
            return false;
        }

        return true;
    }

    const startEndDiff = !!startTime && !!endTime ? differenceInMinutes(endTime.toDate(), startTime.toDate()) : 0;
    const breakDiff = !!breakStart && !!breakEnd ? differenceInMinutes(breakEnd.toDate(), breakStart.toDate()) : 0;
    const totalDiff = (startEndDiff - breakDiff) / 60;

    return (
        <>
            <PillButton onClick={() => setIsOpen(true)} icon={<Add sx={{ fontSize: 18 }} />}>
                Add Hours
            </PillButton>
            {isOpen && (
                <Modal title="Add Hours for Tandem Worker" onClose={onClose}>
                    <Stack gap="md">
                        <Select
                            placeholder="Select Location"
                            value={selectedLocation?.id ?? ''}
                            options={businessLocations?.results.map((x) => ({ key: x.id, value: x.name })) ?? []}
                            onChange={(value) =>
                                setSelectedLocation(
                                    businessLocations?.results.find((x) => String(x.id) === String(value)),
                                )
                            }
                            fullWidth
                        />
                        <AutoComplete
                            multiple={false}
                            options={pastWorkers}
                            selected={selectedWorker}
                            onSelect={(value) =>
                                Array.isArray(value) ? setSelectedWorker(value[0]) : setSelectedWorker(value)
                            }
                            getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                            getOptionKey={(option) => option.id}
                            getOptionSelected={(option, value) => option.id === value.id}
                            getOptionDisabled={() => false}
                            renderOption={(option) => (
                                <Text>
                                    {option.first_name} {option.last_name}
                                </Text>
                            )}
                        />
                        <FieldGroup label="Date">
                            <DatePicker
                                value={date ? date.format('YYYY-MM-DD') : ''}
                                onChange={(value) => setDate(value ? moment(value) : null)}
                                minDate={new Date(new Date().setDate(new Date().getDate() - 30))}
                                maxDate={new Date()}
                                fullWidth
                            />
                        </FieldGroup>
                        <Grid columns={2} gap="md" collapseAt="sm">
                            <FieldGroup label="Start Time">
                                <TimePicker
                                    value={startTime ? startTime.format('HH:mm') : ''}
                                    onChange={(value) => setStartTime(timeStringToMoment(value, startTime))}
                                />
                            </FieldGroup>
                            <FieldGroup label="End Time">
                                <TimePicker
                                    value={endTime ? endTime.format('HH:mm') : ''}
                                    onChange={(value) => setEndTime(timeStringToMoment(value, endTime))}
                                />
                            </FieldGroup>
                            <FieldGroup label="Break Start">
                                <TimePicker
                                    value={breakStart ? breakStart.format('HH:mm') : ''}
                                    onChange={(value) => setBreakStart(timeStringToMoment(value, breakStart))}
                                />
                            </FieldGroup>
                            <FieldGroup label="Break End">
                                <TimePicker
                                    value={breakEnd ? breakEnd.format('HH:mm') : ''}
                                    onChange={(value) => setBreakEnd(timeStringToMoment(value, breakEnd))}
                                />
                            </FieldGroup>
                        </Grid>
                        <Text size="xs">Total Hours Worked: {totalDiff.toFixed(2)}</Text>
                        <Row justify="end" gap="sm">
                            <PillButton variant="secondary" onClick={onClose}>
                                Cancel
                            </PillButton>
                            <PillButton
                                disabled={submitting || !areTimeValid() || !selectedLocation || !selectedWorker}
                                onClick={onAdd}
                            >
                                {submitting ? 'Submitting...' : 'Submit'}
                            </PillButton>
                        </Row>
                        {!areTimeValid() && (
                            <Text tone="error">
                                Times are invalid. Make sure start is before end, both breaks are between the start and
                                end times, and the break start is before the break end.
                            </Text>
                        )}
                        {errorMessage && <Text tone="error">{errorMessage}</Text>}
                    </Stack>
                </Modal>
            )}
        </>
    );
}
